import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Loader2,
  Scale,
  Search,
} from "lucide-react";

import Layout from "../components/Layout";
import { DADOS } from "../lib/larguras";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

function formatDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("pt-BR", { timeZone: "UTC" });
}

function QuotaSummary({ quota, loading }) {
  if (loading && !quota) {
    return <div className="h-16 w-64 rounded-lg bg-[#e5edf5] animate-pulse" />;
  }
  if (!quota) return null;

  const exhausted = quota.remaining === 0;
  return (
    <div
      data-testid="jurisprudencia-quota"
      className={`rounded-lg border px-4 py-3 ${
        exhausted ? "border-amber-300 bg-amber-50" : "border-[#D1DEEE] bg-white"
      }`}
    >
      <div className="flex items-baseline gap-2">
        <strong className={`text-xl ${exhausted ? "text-amber-800" : "text-[#003882]"}`}>
          {quota.remaining}
        </strong>
        {" "}
        <span className="text-sm font-semibold text-[#001533]">
          {quota.remaining === 1 ? "consulta restante" : "consultas restantes"}
        </span>
      </div>
      <p className="mt-0.5 text-xs text-[#335480]">
        {quota.plan === "pro"
          ? `${quota.used} de ${quota.limit} utilizadas neste mês`
          : `${quota.used} de ${quota.limit} utilizadas no total`}
      </p>
    </div>
  );
}

function ResultCard({ item }) {
  const publicationDate = formatDate(item.data_publicacao);
  const judgmentDate = formatDate(item.data_julgamento);
  const tribunal = item.tribunal?.sigla || item.tribunal?.nome;

  return (
    <article className="rounded-lg border border-[#D1DEEE] bg-white p-5" data-testid="jurisprudencia-result">
      <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-[#335480]">
        {item.tipo && <span className="rounded-full bg-[#e5edf5] px-2.5 py-1 text-[#003882]">{item.tipo}</span>}
        {tribunal && <span>{tribunal}</span>}
        {item.origem?.sigla && <span>• {item.origem.sigla}</span>}
      </div>

      <h2 className="mt-3 text-lg font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
        {[item.recurso_nome, item.codigo].filter(Boolean).join(" — ") || "Decisão judicial"}
      </h2>

      {item.ementa ? (
        <div className="mt-4 rounded-lg bg-[#F4F7FB] p-4">
          <p className="mb-1 text-xs font-bold uppercase tracking-wide text-[#335480]">Ementa</p>
          <p className="whitespace-pre-wrap text-sm leading-6 text-[#001533]">{item.ementa}</p>
        </div>
      ) : item.decisao ? (
        <p className="mt-4 whitespace-pre-wrap text-sm leading-6 text-[#001533]">{item.decisao}</p>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-[#E5EDF5] pt-3">
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#335480]">
          {publicationDate && <span>Publicação: {publicationDate}</span>}
          {judgmentDate && <span>Julgamento: {judgmentDate}</span>}
          {item.orgao_julgador && <span>{item.orgao_julgador}</span>}
        </div>
        {item.link && (
          <a
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#003882] hover:text-[#00285e]"
          >
            Ver na fonte <ExternalLink size={14} aria-hidden="true" />
          </a>
        )}
      </div>
    </article>
  );
}

export default function JurisprudenciasPage() {
  const [term, setTerm] = useState("");
  const [searchedTerm, setSearchedTerm] = useState("");
  const [items, setItems] = useState([]);
  const [paginator, setPaginator] = useState(null);
  const [quota, setQuota] = useState(null);
  const [loadingQuota, setLoadingQuota] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/jurisprudencias/quota`, { withCredentials: true })
      .then((response) => {
        if (active) setQuota(response.data);
      })
      .catch(() => {
        if (active) setError("Não foi possível carregar sua cota de pesquisas.");
      })
      .finally(() => {
        if (active) setLoadingQuota(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const performSearch = async (page, query = searchedTerm || term.trim()) => {
    if (query.length < 3 || searching || quota?.remaining === 0) return;

    setSearching(true);
    setError("");
    try {
      const response = await axios.post(
        `${API}/jurisprudencias/search`,
        { termo: query, pagina: page },
        { withCredentials: true }
      );
      setSearchedTerm(query);
      setItems(response.data.items || []);
      setPaginator(response.data.paginator || null);
      setQuota(response.data.quota || quota);
      setHasSearched(true);
    } catch (requestError) {
      const detail = requestError.response?.data?.detail;
      if (detail?.quota) {
        setQuota(detail.quota);
      } else {
        try {
          const quotaResponse = await axios.get(`${API}/jurisprudencias/quota`, {
            withCredentials: true,
          });
          setQuota(quotaResponse.data);
        } catch {
          // Em uma falha ambígua, preservamos a mensagem conservadora abaixo.
        }
      }
      if (detail?.quota_consumed === false) {
        const message = detail.message || "Pesquisa de jurisprudência indisponível no momento.";
        setError(`${message} A consulta não será contabilizada.`);
      } else {
        setError(
          detail?.message ||
            "Não foi possível confirmar o resultado da pesquisa. Confira sua cota antes de tentar novamente."
        );
      }
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const query = term.trim();
    if (query.length < 3) {
      setError("Digite pelo menos 3 caracteres para pesquisar.");
      return;
    }
    performSearch(1, query);
  };

  const exhausted = quota?.remaining === 0;
  const currentPage = paginator?.current_page || 1;
  const totalPages = paginator?.total_pages || 1;

  return (
    <Layout>
      <div className={DADOS}>
        <header className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2 text-[#003882]">
              <Scale size={20} aria-hidden="true" />
              <span className="text-sm font-semibold">Pesquisa jurídica</span>
            </div>
            <h1 className="text-3xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
              Jurisprudências
            </h1>
            <p className="mt-2 max-w-2xl text-base text-[#335480]">
              Encontre decisões judiciais por tema, tese, tribunal ou expressão jurídica.
            </p>
          </div>
          <QuotaSummary quota={quota} loading={loadingQuota} />
        </header>

        <section className="rounded-lg border border-[#D1DEEE] bg-white p-5 sm:p-6">
          <form
            onSubmit={handleSubmit}
            data-testid="jurisprudencia-search-form"
            className="flex flex-col gap-3 sm:flex-row"
          >
            <label className="sr-only" htmlFor="jurisprudencia-search">Termo da pesquisa</label>
            <div className="relative flex-1">
              <Search
                size={19}
                aria-hidden="true"
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[#6685AD]"
              />
              <input
                id="jurisprudencia-search"
                data-testid="jurisprudencia-search-input"
                value={term}
                onChange={(event) => setTerm(event.target.value)}
                placeholder='Ex.: "responsabilidade civil" dano moral'
                disabled={searching || exhausted}
                className="h-12 w-full rounded-lg border border-[#B8C9DF] bg-white pl-11 pr-4 text-base text-[#001533] outline-none transition-colors placeholder:text-[#6685AD] focus:border-[#003882] focus:ring-2 focus:ring-[#003882]/15 disabled:bg-[#F4F7FB]"
              />
            </div>
            <button
              type="submit"
              data-testid="jurisprudencia-search-button"
              disabled={searching || exhausted}
              className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-[#003882] px-6 text-base font-semibold text-white transition-colors hover:bg-[#00285e] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {searching ? <Loader2 size={18} className="animate-spin" aria-hidden="true" /> : <Search size={18} aria-hidden="true" />}
              {searching ? "Pesquisando..." : "Pesquisar"}
            </button>
          </form>
          <p className="mt-3 text-xs text-[#6685AD]">
            Cada busca ou página consultada utiliza 1 consulta da sua cota.
          </p>
        </section>

        {error && (
          <div role="alert" className="mt-5 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            <AlertCircle size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
            <div>
              <p>{error}</p>
              {exhausted && quota?.plan === "free" && (
                <a href="/plano" className="mt-1 inline-block font-semibold text-[#003882] hover:text-[#00285e]">
                  Conhecer o plano PRO
                </a>
              )}
            </div>
          </div>
        )}

        {exhausted && !error && (
          <div className="mt-5 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            Você utilizou todas as consultas disponíveis no plano {quota.plan === "pro" ? "PRO neste mês" : "FREE"}.
            {quota.plan === "free" && (
              <a href="/plano" className="ml-1 font-semibold text-[#003882] hover:text-[#00285e]">Conheça o PRO.</a>
            )}
          </div>
        )}

        {hasSearched && !searching && (
          <section className="mt-7" aria-live="polite">
            <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
              <div>
                <h2 className="text-xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
                  Resultados
                </h2>
                <p className="mt-1 text-sm text-[#335480]">
                  {paginator?.total || items.length} resultado(s) para “{searchedTerm}”
                </p>
              </div>
              {totalPages > 1 && <span className="text-sm text-[#335480]">Página {currentPage} de {totalPages}</span>}
            </div>

            {items.length > 0 ? (
              <div className="space-y-4">
                {items.map((item, index) => (
                  <ResultCard key={`${item.codigo || "resultado"}-${index}`} item={item} />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-[#D1DEEE] bg-white p-8 text-center">
                <p className="font-semibold text-[#001533]">Nenhuma jurisprudência encontrada.</p>
                <p className="mt-1 text-sm text-[#335480]">Tente pesquisar com termos mais amplos ou outra expressão.</p>
              </div>
            )}

            {totalPages > 1 && (
              <nav className="mt-6 flex items-center justify-center gap-3" aria-label="Paginação das jurisprudências">
                <button
                  type="button"
                  onClick={() => performSearch(currentPage - 1)}
                  disabled={searching || currentPage <= 1 || exhausted}
                  className="inline-flex h-10 items-center gap-1 rounded-lg border border-[#B8C9DF] px-3 text-sm font-semibold text-[#003882] hover:bg-[#F4F7FB] disabled:opacity-40"
                >
                  <ChevronLeft size={17} aria-hidden="true" /> Anterior
                </button>
                <button
                  type="button"
                  onClick={() => performSearch(currentPage + 1)}
                  disabled={searching || currentPage >= totalPages || exhausted}
                  className="inline-flex h-10 items-center gap-1 rounded-lg border border-[#B8C9DF] px-3 text-sm font-semibold text-[#003882] hover:bg-[#F4F7FB] disabled:opacity-40"
                >
                  Próxima <ChevronRight size={17} aria-hidden="true" />
                </button>
              </nav>
            )}
          </section>
        )}

        {!hasSearched && !searching && (
          <section className="mt-7 rounded-lg bg-[#F4F7FB] p-6">
            <h2 className="text-base font-bold text-[#001533]">Como pesquisar melhor</h2>
            <ul className="mt-3 grid gap-2 text-sm text-[#335480] sm:grid-cols-3">
              <li>• Use os principais termos da tese jurídica.</li>
              <li>• Inclua tribunal ou tipo de recurso quando necessário.</li>
              <li>• Use aspas para buscar uma expressão exata.</li>
            </ul>
          </section>
        )}
      </div>
    </Layout>
  );
}
